import { useEffect, useState } from 'react'
import { Loader2, RefreshCw, Search } from 'lucide-react'
import api from '../../utils/api'
import PackageSearchSelect from './PackageSearchSelect'

export default function UserReminderClickLogsTab() {
  const [loading, setLoading] = useState(false)
  const [packagesLoading, setPackagesLoading] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [phone, setPhone] = useState('')
  const [downloadSources, setDownloadSources] = useState([])
  const [sourceOptions, setSourceOptions] = useState([])
  const [routeKey, setRouteKey] = useState('')
  const [clickAction, setClickAction] = useState('')
  const [conversionStatus, setConversionStatus] = useState('')
  const [records, setRecords] = useState([])
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 })
  const [stats, setStats] = useState({ total: 0, openTotal: 0, openRate: 0, convertedTotal: 0, convertedRate: 0 })
  const [jumpPage, setJumpPage] = useState('')

  const fetchSourceOptions = async () => {
    setPackagesLoading(true)
    try {
      const res = await api.get('/admin/download-package-names')
      if (res.data.success) {
        const data = res.data.data || res.data || {}
        const labels = Array.from(
          new Set((data.list || []).map((item) => String(item?.label || '').trim()).filter(Boolean))
        )
        setSourceOptions([
          ...labels.map((label, index) => ({
            id: `label-${index}`,
            value: label,
            label
          })),
          {
            id: 'unknown-user',
            value: '__unknown__',
            label: '未知用户'
          },
          {
            id: 'abnormal-client',
            value: '__abnormal__',
            label: '客户端异常登录'
          }
        ])
      }
    } catch (e) {
      console.error('获取下载来源失败:', e)
    } finally {
      setPackagesLoading(false)
    }
  }

  const fetchData = async (page = 1, targetPhone = phone, targetDownloadSources = downloadSources, filters = {}) => {
    const trimmedPhone = String(targetPhone || '').trim()
    const normalizedDownloadSources = Array.isArray(targetDownloadSources)
      ? targetDownloadSources.map((item) => String(item || '').trim()).filter(Boolean)
      : []
    const currentRouteKey = filters.routeKey !== undefined ? filters.routeKey : routeKey
    const currentClickAction = filters.clickAction !== undefined ? filters.clickAction : clickAction
    const currentConversionStatus = filters.conversionStatus !== undefined ? filters.conversionStatus : conversionStatus
    setLoading(true)
    setPhone(trimmedPhone)
    setDownloadSources(normalizedDownloadSources)
    setRouteKey(currentRouteKey)
    setClickAction(currentClickAction)
    setConversionStatus(currentConversionStatus)

    try {
      const response = await api.get('/admin/user-reminder-click-logs', {
        params: {
          phone: trimmedPhone,
          downloadSources: normalizedDownloadSources.length > 0 ? normalizedDownloadSources : undefined,
          routeKey: currentRouteKey || undefined,
          clickAction: currentClickAction || undefined,
          conversionStatus: currentConversionStatus || undefined,
          page,
          limit: pagination.limit
        }
      })

      if (response.data.success) {
        const data = response.data.data || response.data || {}
        const pager = data.pagination || {}

        setRecords(data.list || [])
        setStats({
          total: Number(data.stats?.total || 0),
          openTotal: Number(data.stats?.openTotal || 0),
          openRate: Number(data.stats?.openRate || 0),
          convertedTotal: Number(data.stats?.convertedTotal || 0),
          convertedRate: Number(data.stats?.convertedRate || 0)
        })
        setPagination((prev) => ({
          page: pager.page || 1,
          limit: pager.limit || prev.limit,
          total: pager.total || 0,
          totalPages: pager.totalPages || 1
        }))
      }
    } catch (error) {
      console.error('获取用户弹窗点击情况失败:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSourceOptions()
    fetchData(1, '', [], { routeKey: '', clickAction: '', conversionStatus: '' })
  }, [])

  const handleSearch = (e) => {
    e.preventDefault()
    fetchData(1, searchInput, downloadSources)
  }

  const handleReset = () => {
    setSearchInput('')
    setDownloadSources([])
    setRouteKey('')
    setClickAction('')
    setConversionStatus('')
    setJumpPage('')
    fetchData(1, '', [], { routeKey: '', clickAction: '', conversionStatus: '' })
  }

  const handleJump = () => {
    const page = Number(jumpPage)
    if (!jumpPage.trim() || isNaN(page)) {
      setJumpPage('')
      return
    }
    if (page < 1 || page > pagination.totalPages) {
      alert(`请输入 1 ~ ${pagination.totalPages} 之间的页码`)
      return
    }
    fetchData(page, phone, downloadSources)
    setJumpPage('')
  }

  const getDownloadSourceText = (item) => {
    if (item?.download_source_label) {
      return item.download_source_label
    }

    if (item?.first_installer_name) {
      return '未知用户'
    }

    return '客户端异常登录'
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">用户弹窗点击</h2>

        <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="手机号搜索"
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
            />
          </div>
          <PackageSearchSelect
            value={downloadSources}
            onChange={setDownloadSources}
            options={sourceOptions}
            loading={packagesLoading}
            multiple
            placeholder="全部来源"
            className="w-full sm:w-64 min-w-0"
            getOptionValue={(item) => item.value}
            getOptionDisplayName={(item) => item.label}
            searchPlaceholder="搜索渠道来源"
          />
          <select
            value={routeKey}
            onChange={(e) => setRouteKey(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          >
            <option value="">全部路线</option>
            <option value="unactivated">未激活用户</option>
            <option value="activated_low_cost_15d">低消耗用户</option>
            <option value="low_balance_unpaid">低余额用户</option>
          </select>
          <select
            value={clickAction}
            onChange={(e) => setClickAction(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          >
            <option value="">是否打开客户端</option>
            <option value="open">已打开</option>
            <option value="not_open">未打开</option>
          </select>
          <select
            value={conversionStatus}
            onChange={(e) => setConversionStatus(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          >
            <option value="">是否对话</option>
            <option value="converted">已对话</option>
            <option value="pending">未对话</option>
          </select>
          <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
            查询
          </button>
          <button type="button" onClick={handleReset} className="rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200">
            重置
          </button>
          <button
            type="button"
            onClick={() => fetchData(pagination.page, phone, downloadSources)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
        </form>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">弹窗总数</div>
          <div className="mt-2 text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">打开客户端</div>
          <div className="mt-2 text-2xl font-bold text-green-600">
            {stats.openTotal} ({stats.openRate}%)
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">对话转换数量</div>
          <div className="mt-2 text-2xl font-bold text-blue-600">
            {stats.convertedTotal} ({stats.convertedRate}%)
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 bg-white px-6 py-4 text-sm text-gray-500">
            当前筛选手机号：{phone || '全部'}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">点击时间</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">弹窗时间</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">最新使用时间</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">点击事件</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">对话转换</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">用户UID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">手机号</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">路线/页面</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">下载来源</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {records.length > 0 ? (
                  records.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{item.clicked_at || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.reminder_shown_at || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.latest_usage_at || '-'}</td>
                      <td
                        className={`px-6 py-4 text-sm font-medium ${
                          item.click_action === 'open'
                            ? 'text-green-600'
                            : item.click_action === 'close'
                              ? 'text-red-600'
                              : 'text-gray-900'
                        }`}
                      >
                        {item.click_action_label || '-'}
                      </td>
                      <td className={`px-6 py-4 text-sm font-medium ${item.conversion_status === 'converted' ? 'text-green-600' : 'text-red-600'}`}>
                        {item.conversion_status === 'converted' ? '已对话' : '未对话'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.user_id || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.phone || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.route_label ? `${item.route_label} / ${item.page_key || '-'} / 第${item.stage_no || '-'}次` : '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{getDownloadSourceText(item)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="px-6 py-10 text-center text-sm text-gray-500">
                      暂无弹窗点击记录
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between bg-gray-50 px-6 py-4">
            <div className="text-sm text-gray-600">
              共 {pagination.total} 条，第 {pagination.page}/{pagination.totalPages} 页
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={jumpPage}
                onChange={(e) => setJumpPage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                placeholder="页码"
                min={1}
                max={pagination.totalPages}
                className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <button
                onClick={handleJump}
                className="rounded-lg border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                跳转
              </button>
              <button
                disabled={pagination.page <= 1}
                onClick={() => fetchData(pagination.page - 1, phone, downloadSources)}
                className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                上一页
              </button>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => fetchData(pagination.page + 1, phone, downloadSources)}
                className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
